/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useRef, useState } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

import styles from "./MenuDropdown.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import Portal from "@Components/Portal";

interface IProps {
  /** Static target. Optional only because `renderTarget` can supply it instead. */
  target?: any;
  /**
   * Alternative to `target` for a target that needs to know whether the menu is open —
   * a chevron that turns, for instance. Called with the open state; when supplied it
   * replaces `target`, which every existing caller keeps using unchanged.
   */
  renderTarget?: (isOpen: boolean) => any;
  menu: any;
  menuContainerClassName?: any;
  direction?: "left" | "right";
  addFixed?: boolean;
  forceClose?: any;
  handleOnClose?: any;
  dataTestId?: string;
  filterTestId?: string;
  hidden?: boolean;
  closeOnSelect?: boolean;
}
const MenuDropdown = (props: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    target,
    renderTarget,
    menu,
    direction,
    addFixed,
    forceClose,
    handleOnClose,
    dataTestId,
    filterTestId,
    hidden,
    closeOnSelect,
  } = props;
  const dropdownRef = useRef<any>(null);
  const contentRef = useRef<any>(null);

  useEffect(() => {
    if (forceClose) setIsOpen(false);
  }, [forceClose]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (typeof handleOnClose === "function" && isOpen) handleOnClose();
  };

  const handleKeyPress = (e: any) => {
    if (e.keyCode === 27) {
      // Esc
      setIsOpen(false);
      if (typeof handleOnClose === "function" && isOpen) handleOnClose();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (typeof handleOnClose === "function") handleOnClose();
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      positionDropdown();
    }
  }, [isOpen]);

  // Reposition when content size changes (e.g. Suspense fallback replaced by actual content)
  useEffect(() => {
    if (!isOpen || !contentRef.current) return undefined;
    const observer = new ResizeObserver(() => positionDropdown());
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [isOpen]);

  // Reposition on scroll/resize when using fixed/portal rendering
  useEffect(() => {
    if (isOpen && addFixed) {
      const handler = () => positionDropdown();
      window.addEventListener("scroll", handler, true);
      window.addEventListener("resize", handler);
      return () => {
        window.removeEventListener("scroll", handler, true);
        window.removeEventListener("resize", handler);
      };
    }
    return undefined;
  }, [isOpen, addFixed]);

  const positionDropdown = () => {
    if (!contentRef.current) return;
    if (direction) {
      if (direction === "left") {
        contentRef.current.style.right = 0 as any;
        contentRef.current.style.left = "auto";
      } else {
        contentRef.current.style.left = 0 as any;
        contentRef.current.style.right = "auto";
      }
    } else if (dropdownRef.current && contentRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();
      const contentWidth = contentRect.width || contentRef.current.offsetWidth;

      // If content hasn't rendered yet, defer positioning.
      if (contentWidth === 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => positionDropdown());
        });
        return;
      }

      const spaceRight = window.innerWidth - dropdownRect.left;
      const spaceLeft = dropdownRect.right;
      const overflowsRight = spaceRight < contentWidth;
      const fitsLeft = spaceLeft >= contentWidth;

      // Horizontal alignment
      if (overflowsRight && fitsLeft) {
        // Align dropdown's right edge to trigger's right edge
        if (addFixed) {
          contentRef.current.style.left = "auto";
          contentRef.current.style.right = `${window.innerWidth - dropdownRect.right}px`;
        } else {
          contentRef.current.style.right = 0 as any;
          contentRef.current.style.left = "auto";
        }
      } else if (addFixed) {
        // Align dropdown's left edge to trigger's left edge
        const leftPos = dropdownRect.left + window.scrollX;
        // Guard against right-side overflow
        if (leftPos + contentWidth > window.innerWidth) {
          contentRef.current.style.left = "auto";
          contentRef.current.style.right = `${window.innerWidth - dropdownRect.right}px`;
        } else {
          contentRef.current.style.left = `${leftPos}px`;
          contentRef.current.style.right = "auto";
        }
      } else {
        contentRef.current.style.left = 0 as any;
        contentRef.current.style.right = "auto";
      }

      // Vertical positioning when fixed
      if (addFixed) {
        contentRef.current.style.top = `${dropdownRect.bottom}px`;
      }
    }
  };

  return (
    <div
      className={formatClassNames(styles.container, hidden && styles.hidden)}
      ref={dropdownRef}
      data-testid={dataTestId}
    >
      <div
        className={styles.target}
        role="button"
        onClick={() => {
          toggleDropdown();
        }}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            toggleDropdown();
          }
        }}
        data-testid={filterTestId}
      >
        {renderTarget ? renderTarget(isOpen) : target}
      </div>
      {isOpen &&
        (addFixed ? (
          <Portal>
            <div
              className={formatClassNames(
                styles.menu,
                styles.addFixed,
                props?.menuContainerClassName,
              )}
              ref={contentRef}
              style={{ position: "fixed" } as any}
              onClick={(e) => {
                e.stopPropagation();
                if (closeOnSelect) {
                  setIsOpen(false);
                  if (typeof handleOnClose === "function") handleOnClose();
                }
              }}
            >
              {menu}
            </div>
          </Portal>
        ) : (
          <div
            className={formatClassNames(styles.menu, props?.menuContainerClassName)}
            ref={contentRef}
            onClick={(e) => {
              e.stopPropagation();
              if (closeOnSelect) {
                setIsOpen(false);
                if (typeof handleOnClose === "function") handleOnClose();
              }
            }}
          >
            {menu}
          </div>
        ))}
    </div>
  );
};

export default wrapperHOC(MenuDropdown, {
  componentName: "MenuDropdown-Global",
  showForChina: true,
});
